import {
  forkJoin as observableForkJoin,
  of as observableOf,
  Observable,
  Subscription,
  ConnectableObservable,
  forkJoin,
  observable,
  of,
} from 'rxjs';

import {publishReplay, map, catchError, first, mergeMap} from 'rxjs/operators';
import {Component, forwardRef, Inject, OnDestroy} from '@angular/core';
import {Resource} from '../resource';
import {ResourceService} from '../resource.service';
import {FilterResult} from '../filterresult';
import {MatDialogRef} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {LanguageService} from '../../language/language.service';
import {CacheService} from '../../cache/cache.service';
import {DialogsService} from '../../shared/dialog/dialogs.service';
import {EnvironmentConfigService} from '../../core/services/environment-config.service';
import {LocalDatePipe} from '../../shared/pipes/local-date.pipe';
import {ResourceComponent} from '../resource.component';

@Component({
  selector: 'request-dialog',
  templateUrl: './request-dialog.component.html',
  styleUrls: ['./request-dialog.component.scss'],
  standalone: false,
})
export class RequestDialogComponent implements OnDestroy {
  currentRequestIndex: number = 0;
  requests: Resource[];
  reason: string = '';
  reasonCaption: string = 'Reason';
  loading: boolean = true;

  // Getter for template access to environment attributes
  get myenv() {
    return {
      Attributes: {
        ObjectID: this.envConfig.getAttributeName('ObjectID'),
        DisplayName: this.envConfig.getAttributeName('DisplayName'),
        Creator: this.envConfig.getAttributeName('Creator'),
        CreatedTime: this.envConfig.getAttributeName('CreatedTime'),
        RequestStatus: this.envConfig.getAttributeName('RequestStatus'),
        ApprovalStatus: this.envConfig.getAttributeName('ApprovalStatus'),
        Reason: this.envConfig.getAttributeName('Reason'),
        GrantRight: this.envConfig.getAttributeName('GrantRight'),
      },
    };
  }

  private observableCache: ConnectableObservable<string>[] = [];
  private observableConnections: Subscription[] = [];

  constructor(
    public dialogRef: MatDialogRef<RequestDialogComponent>,
    private resourceService: ResourceService,
    private cacheService: CacheService,
    private dialogsService: DialogsService,
    private snackBar: MatSnackBar,
    private envConfig: EnvironmentConfigService,
  ) {
    this.cacheService
      .getLanguageResource()
      .pipe(first())
      .subscribe(lr => {
        this.reasonCaption = LanguageService.translate('RequestReasonCaption', 'Reason', lr);
      });
  }

  private alive: boolean = true;

  ngOnDestroy(): void {
    this.alive = false;
    this.observableConnections.forEach((s: Subscription) => s.unsubscribe());
  }

  get currentRequest(): Resource {
    if (this.requests.length > 0 && this.currentRequestIndex < this.requests.length) {
      return this.requests[this.currentRequestIndex];
    } else {
      return null;
    }
  }

  public init(guids: string[]): void {
    if (guids == null || !Array.isArray(guids) || guids.length === 0) {
      this.loading = false;
      return;
    }

    const resourceObs = guids
      .filter(guid => ResourceService.guidRegex.test(guid))
      .map(guid =>
        this.resourceService
          .searchResources(
            '/' +
              this.envConfig.getObjectTypeName('Approval') +
              '[' +
              this.envConfig.getAttributeName('ObjectID') +
              "='" +
              guid +
              "']",
            this.envConfig.getAttributeName('Request') + ',ApprovalStatus',
            1,
          )
          .pipe(
            catchError((error, caught) => {
              this.loading = false;
              return observableOf(null);
            }),
            mergeMap((result: FilterResult) => {
              if (result == null || result.TotalCount !== 1 || result.Results.length !== 1) {
                return observableOf(null);
              }

              const app = result.Results[0];

              if (app['ApprovalStatus'] !== 'Pending') {
                return observableOf(null);
              }

              return this.resourceService
                .searchResources(
                  '/' +
                    this.envConfig.getObjectTypeName('Request') +
                    '[' +
                    this.envConfig.getAttributeName('ObjectID') +
                    "='" +
                    app[this.envConfig.getAttributeName('Request')] +
                    "']",
                  [
                    this.envConfig.getAttributeName('DisplayName'),
                    this.envConfig.getAttributeName('Target'),
                    this.envConfig.getAttributeName('Creator'),
                    this.envConfig.getAttributeName('CreatedTime'),
                    this.envConfig.getAttributeName('RequestParameter'),
                    this.envConfig.getAttributeName('RequestStatus'),
                  ].join(','),
                  1,
                )
                .pipe(
                  catchError((error, caught) => {
                    this.loading = false;
                    return observableOf(null);
                  }),
                );
            }),
            map((fr: FilterResult) => {
              if (fr != null && fr.TotalCount === 1 && fr.Results.length === 1) {
                const req = fr.Results[0];
                if (req.ObjectType === this.envConfig.getObjectTypeName('Request')) {
                  req['Approval'] = guid;
                  return req;
                } else {
                  return null;
                }
              }
            }),
          ),
      );

    this.requests = [];
    this.currentRequestIndex = 0;
    resourceObs.forEach(obs => {
      obs.subscribe(r => {
        if (r) {
          this.requests.push(r);
        }
        this.loading = false; // show already first result, don't wait for all
      });
    });
  }

  getCreator(creator: string): Observable<string> {
    if (creator == null || creator.length === 0) return observableOf('');
    let valueObs = this.observableCache[creator];
    if (valueObs == null) {
      valueObs = this.resourceService.getBasicResource(creator).pipe(
        map(r => r[this.envConfig.getAttributeName('DisplayName')]),
        publishReplay(1),
      );
      this.observableConnections.push(valueObs.connect());
      this.observableCache[creator] = valueObs;
    }
    return valueObs;
  }

  openDetail(guid: string): void {
    // GUID casing from the parsed request XML is not guaranteed
    if (guid == null || !ResourceService.guidRegex.test(guid.toLowerCase())) return;
    window.open('#/get/' + guid);
  }

  skip(): void {
    if (this.currentRequestIndex + 1 >= this.requests.length) {
      this.dialogRef.close();
    } else {
      this.currentRequestIndex++;
    }
  }

  submit(decision: 'approve' | 'reject'): void {
    this.loading = true;
    this.resourceService
      .requestApproval(this.currentRequest['Approval'], decision, this.reason)
      .pipe(catchError(() => of(false)))
      .subscribe(result => {
        this.cacheService
          .getLanguageResource()
          .pipe(first())
          .subscribe(lr => {
            if (result === true) {
              const message = LanguageService.translate(
                'RequestSubmitSuccessMessage',
                'Your request decision has been submitted successfully.',
                lr,
              );
              this.snackBar.open(message, undefined, {duration: 5000});
            } else {
              const title = LanguageService.translate(
                'RequestSubmitErrorTitle',
                'Error submitting request decision',
                lr,
              );
              let message = LanguageService.translate(
                'RequestSubmitErrorMessageSingle',
                'One request decision could not be submitted.',
                lr,
              );

              this.dialogsService.error(title, message, null);
            }
          });
        if (result === true) {
          this.skip();
        }
        this.loading = false;
      });
  }
}
